import { ArrowDown, ArrowUp, Copy, Link2, Pencil, Plus, Search, Trash2, Users } from "lucide-react"
import { isQuietLink } from "../model/quiet-link"
import { formatRelative } from "@/entities/audience"
import { Badge } from "@/shared/ui/badge"
import { Button } from "@/shared/ui/button"
import { EmptyState } from "@/shared/ui/empty-state"
import { Field } from "@/shared/ui/form-field"
import { Input } from "@/shared/ui/input"
import { Label } from "@/shared/ui/label"
import { NativeSelect } from "@/shared/ui/native-select"
import { RowActionsMenu } from "@/shared/ui/row-actions-menu"
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
} from "@/shared/ui/sheet"
import { Switch } from "@/shared/ui/switch"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table"
import { Textarea } from "@/shared/ui/textarea"
import type { UtmLinksState, UtmSortKey } from "../model/use-utm-links"
import { AutoHeight } from "@/shared/ui/auto-height"
import { LoadSwap } from "@/shared/ui/load-swap"

/** Таблица меток и форма правки: состояние и мутации держит useUtmLinks. */
export function UtmLinksContent(utm: UtmLinksState) {
	const {
		draft,
		search,
		setSearch,
		query,
		links,
		editing,
		form,
		codeValid,
		stats,
		statsByCode,
		sort,
		sortBy,
		flowName,
		linkUrl,
		save,
		setForm,
		setName,
		openEdit,
		closeEditing,
		copyLink,
		openPeople,
		setActive,
		deleteLink,
		openCreate,
	} = utm

	return (
		<>
			<div className="relative mb-3">
				<Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
				<Input
					aria-label="Поиск метки"
					placeholder="Название или метка"
					className="h-10 pl-9"
					value={search}
					onChange={(event) => setSearch(event.target.value)}
				/>
			</div>

			{/* «Пусто» и список сменяют друг друга перекрёстно, высота тянется к новой. */}
			<LoadSwap loading={false} id={links.length === 0 ? "empty" : "list"}>
				{links.length === 0 ? (
					query ? (
						<EmptyState icon={Link2} title="Ничего не нашлось" />
					) : (
						<EmptyState
							icon={Link2}
							title="Меток пока нет"
							action={
								<Button className="rounded-xl" onClick={openCreate}>
									<Plus />
									Создать метку
								</Button>
							}
						>
							Метка это ссылка на бота для рекламы или поста: по ней видно, сколько людей пришло и
							что они сделали.
						</EmptyState>
					)
				) : (
					<AutoHeight className="overflow-x-auto rounded-2xl border border-border bg-card">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead className="w-20">Статус</TableHead>
									<SortHead label="Метка" sortKey="name" sort={sort} onSort={sortBy} />
									<TableHead className="hidden md:table-cell">Воронка</TableHead>
									<SortHead label="Перешли" sortKey="count" sort={sort} onSort={sortBy} right />
									<SortHead
										label="Дошли до конца"
										sortKey="finished"
										sort={sort}
										onSort={sortBy}
										right
										className="hidden sm:table-cell"
									/>
									<SortHead
										label="Последний переход"
										sortKey="lastAt"
										sort={sort}
										onSort={sortBy}
										right
										className="hidden lg:table-cell"
									/>
									<TableHead className="w-10" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{links.map((link) => {
									const flow = link.flowId
										? draft.flows.find((item) => item.id === link.flowId)
										: null
									const row = statsByCode.get(link.code)
									const came = row?.count ?? 0
									const finished = row?.finished ?? 0
									return (
										<TableRow key={link.id} className={link.active ? undefined : "opacity-60"}>
											<TableCell>
												<Switch
													aria-label={`Метка ${link.code} включена`}
													checked={link.active}
													onCheckedChange={(active) => setActive(link, active)}
												/>
											</TableCell>
											<TableCell className="max-w-72">
												<button
													type="button"
													className="block max-w-full truncate text-left font-medium hover:underline"
													onClick={() => openPeople(link.code)}
												>
													{link.name}
												</button>
												<Button
													type="button"
													variant="ghost"
													className="h-auto gap-1 p-0 font-mono text-xs text-muted-foreground hover:bg-transparent hover:text-primary"
													title="Скопировать ссылку"
													onClick={() => copyLink(link.code)}
												>
													{link.code}
													<Copy className="h-3 w-3" />
												</Button>
											</TableCell>
											<TableCell className="hidden text-sm md:table-cell">
												{flowName(link.flowId)}
												{flow?.active === false ? (
													<Badge variant="outline" className="ml-1 text-amber-300">
														выключена
													</Badge>
												) : null}
											</TableCell>
											<TableCell className="text-right tabular-nums">
												<Button
													type="button"
													variant="ghost"
													className="h-auto p-0 tabular-nums hover:bg-transparent hover:text-primary hover:underline"
													title="Открыть метку: путь по воронке и люди"
													onClick={() => openPeople(link.code)}
												>
													{stats.isLoading ? "…" : came}
												</Button>
											</TableCell>
											<TableCell className="hidden text-right tabular-nums sm:table-cell">
												{stats.isLoading ? (
													"…"
												) : came > 0 ? (
													<>
														{finished}
														<span className="ml-1 text-xs text-muted-foreground">
															{Math.round((finished / came) * 100)}%
														</span>
													</>
												) : (
													<span className="text-muted-foreground">–</span>
												)}
											</TableCell>
											<TableCell className="hidden text-right text-xs text-muted-foreground lg:table-cell">
												{row ? formatRelative(row.lastAt) : "–"}
												{isQuietLink(link.active, row?.lastAt) ? (
													<span
														className="block text-amber-300"
														title="Проверьте, стоит ли ещё ссылка там, куда её поставили"
													>
														давно без переходов
													</span>
												) : null}
											</TableCell>
											<TableCell>
												<RowActionsMenu
													label={`Действия с ${link.code}`}
													actions={[
														{
															label: "Открыть метку",
															icon: Users,
															onSelect: () => openPeople(link.code),
														},
														{
															label: "Изменить",
															icon: Pencil,
															onSelect: () => openEdit(link),
														},
														{
															label: "Скопировать ссылку",
															icon: Copy,
															onSelect: () => copyLink(link.code),
														},
														{
															label: "Удалить",
															icon: Trash2,
															destructive: true,
															separatorBefore: true,
															onSelect: () => deleteLink(link),
														},
													]}
												/>
											</TableCell>
										</TableRow>
									)
								})}
							</TableBody>
						</Table>
					</AutoHeight>
				)}
			</LoadSwap>

			<Sheet
				open={!!editing}
				onOpenChange={(open) => {
					if (!open) closeEditing()
				}}
			>
				<SheetContent className="w-full overflow-y-auto sm:max-w-md">
					<SheetHeader>
						<SheetTitle>{editing?.link ? "Метка" : "Новая метка"}</SheetTitle>
						<SheetDescription>
							{form?.code && codeValid
								? linkUrl(form.code)
								: "Ссылка появится, когда введёте метку"}
						</SheetDescription>
					</SheetHeader>
					{form ? (
						<form
							className="space-y-4 px-4"
							onSubmit={(event) => {
								event.preventDefault()
								void save()
							}}
						>
							<Field label="Название" htmlFor="utm-name">
								<Input
									id="utm-name"
									placeholder="Например, Реклама у блогера"
									maxLength={200}
									value={form.name}
									onChange={(event) => setName(event.target.value)}
								/>
							</Field>
							<Field
								label="Метка"
								htmlFor="utm-code"
								hint={
									form.code && !codeValid
										? "Латиница, цифры, _ и -, до 64 символов"
										: editing?.link && form.code !== editing.link.code
											? "Старые ссылки с прежней меткой перестанут работать"
											: !editing?.link && !editing?.codeEdited
												? "Подставляется из названия, можно изменить"
												: undefined
								}
							>
								<Input
									id="utm-code"
									maxLength={64}
									placeholder="utm_insta_story"
									value={form.code}
									onChange={(event) => setForm({ code: event.target.value.trim() })}
								/>
							</Field>
							<Field label="Воронка" htmlFor="utm-flow">
								<NativeSelect
									id="utm-flow"
									value={form.flowId ?? ""}
									onChange={(event) => setForm({ flowId: event.target.value || null })}
								>
									<option value="">Основная воронка</option>
									{draft.flows
										.filter((flow) => flow.kind === "CUSTOM")
										.map((flow) => (
											<option key={flow.id} value={flow.id}>
												{flow.name}
												{flow.active === false ? " (выключена)" : ""}
											</option>
										))}
								</NativeSelect>
							</Field>
							<Field label="Комментарий" htmlFor="utm-comment">
								<Textarea
									id="utm-comment"
									placeholder="Где стоит ссылка. Видно только в админке"
									rows={2}
									maxLength={500}
									value={form.comment ?? ""}
									onChange={(event) => setForm({ comment: event.target.value })}
								/>
							</Field>
							<Label className="flex items-center justify-between gap-3 text-sm">
								Метка включена
								<Switch checked={form.active} onCheckedChange={(active) => setForm({ active })} />
							</Label>
							<SheetFooter className="px-0">
								<Button type="submit" disabled={!codeValid || !form.name.trim()}>
									Сохранить
								</Button>
							</SheetFooter>
						</form>
					) : null}
				</SheetContent>
			</Sheet>
		</>
	)
}

function SortHead({
	label,
	sortKey,
	sort,
	onSort,
	right,
	className,
}: {
	label: string
	sortKey: UtmSortKey
	sort: { key: UtmSortKey; desc: boolean }
	onSort: (key: UtmSortKey) => void
	right?: boolean
	className?: string
}) {
	const active = sort.key === sortKey
	const Arrow = sort.desc ? ArrowDown : ArrowUp
	return (
		<TableHead
			className={className}
			aria-sort={active ? (sort.desc ? "descending" : "ascending") : "none"}
		>
			<button
				type="button"
				onClick={() => onSort(sortKey)}
				className={`inline-flex items-center gap-1 hover:text-foreground ${right ? "w-full justify-end" : ""} ${active ? "text-foreground" : ""}`}
			>
				{label}
				{active ? <Arrow className="size-3" /> : null}
			</button>
		</TableHead>
	)
}
